import {
  exportJobSchema,
  fullBackupExportRequestSchema,
  type ExportJob,
  type FullBackupExportRequest,
} from '@complan/contracts';
import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiFetch } from '@/shared/api/client';

/**
 * The Export centre data layer (task 23.5), wired to the task 20.x export API.
 *
 * An export is a job, not a response (R14.11, R15.9): a POST answers with a job in
 * the `queued`/`running` state, and the client polls `GET /api/exports/:jobId` at
 * least every five seconds (R18.4) until the job reaches a terminal state. On
 * `succeeded` the job carries a download token; `failed` and `timed_out` deliver
 * no file and say so through `state` alone — the stored data is unchanged (R14.11,
 * R15.9, R18.17).
 *
 * The polling, the terminal-state predicate and the filtered-view request builder
 * are shared with the calendar's export (task 22.6) rather than duplicated: this
 * module re-exports them from `../calendar/export-data` and adds only the
 * full-backup POST (R14.1), which the calendar screen has no reason to start.
 */
export {
  EXPORT_POLL_INTERVAL_MS,
  fetchExportJob,
  isTerminalExportState,
  toFilteredViewRequest,
  useExportJob,
  useRequestFilteredViewExport,
} from '../calendar/export-data';

/**
 * Enqueues a full-backup export and returns the initial job (R14.1, R18.4).
 *
 * The full backup takes no filters — it covers everything the requesting user may
 * read, and the permission scope it ran under is recorded in the workbook (R14.5).
 * The record cap (R14.9) is enforced server-side before generation; exceeding it
 * comes back as a validation error the caller surfaces, not a job.
 */
export function useRequestFullBackupExport(): UseMutationResult<
  ExportJob,
  unknown,
  FullBackupExportRequest
> {
  return useMutation({
    mutationFn: async (request: FullBackupExportRequest) => {
      const body = await apiFetch<unknown>('/api/exports/full-backup', {
        method: 'POST',
        body: JSON.stringify(fullBackupExportRequestSchema.parse(request)),
      });
      return exportJobSchema.parse(body);
    },
  });
}

/**
 * The URL the completed-job download button targets.
 *
 * ⚠ GAP (flagged by task 22.6 and re-confirmed by task 23.5): the API currently
 * exposes no route that serves the artifact file for a completed job's
 * `downloadToken`. There is `POST /api/exports/full-backup`, `POST
 * /api/exports/filtered-view` and the `GET /api/exports/:jobId` *status* poll, but
 * nothing that streams the `.xlsx`. This builder returns the documented REST shape
 * a serving route would take (`GET /api/exports/:jobId/download?token=…`) so the
 * flow is wired and the shape is agreed, but the screen deliberately does *not*
 * fabricate a fake download: {@link isExportDownloadAvailable} reports the endpoint
 * as absent, and the UI shows a "download not yet available" notice instead of
 * navigating. Flip {@link isExportDownloadAvailable} to `true` once the serving
 * route ships and the button becomes a real download link.
 */
export function exportDownloadUrl(jobId: string, downloadToken: string): string {
  const token = encodeURIComponent(downloadToken);
  return `/api/exports/${encodeURIComponent(jobId)}/download?token=${token}`;
}

/**
 * Whether the export download endpoint that serves a completed job's file exists.
 *
 * `false` until the serving route ships (see {@link exportDownloadUrl}). The UI
 * reads this to decide whether the download control navigates to the real file or
 * surfaces the "not yet available" notice, so no click ever 404s or fabricates a
 * download.
 */
export const isExportDownloadAvailable = false;
